import { AlertTriangle, Lock } from 'lucide-react'
import { useMsal } from '@azure/msal-react'
import { useLogout } from '../auth/authProvider'
import Button from './ui/Button'

/**
 * Pantalla de "sin acceso" en dos variantes:
 *
 * - `no-role` (default): el usuario está autenticado en Entra ID pero NO tiene
 *   ningún App Role asignado (claim `roles` vacío). No puede entrar a la app.
 * - `feature`: el usuario SÍ tiene rol, pero ese rol no incluye acceso a la
 *   sección a la que intentó entrar (p.ej. entró por URL directa a una ruta
 *   que su rol no ve en el menú). Gating estilo contract-ai.
 */
export default function NoAccessScreen({
  variant = 'no-role',
}: {
  variant?: 'no-role' | 'feature'
}) {
  if (variant === 'feature') {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-card-2 text-muted">
          <Lock className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-semibold text-fg">Sin acceso a esta funcionalidad</h2>
        <p className="max-w-sm text-sm text-fg-2 text-pretty">
          Tu rol no incluye acceso a esta sección. Si crees que es un error, contacta al administrador.
        </p>
      </div>
    )
  }

  return <SinRol />
}

/** Autenticado en Entra pero sin App Role (mockup `sin_rol`). */
function SinRol() {
  const { instance } = useMsal()
  const logout = useLogout()
  const email = instance.getActiveAccount()?.username

  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 px-4 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-warn-bg text-warn-fg">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <h2 className="text-lg font-semibold text-fg">Tu cuenta no tiene un rol asignado</h2>
      <p className="max-w-md text-sm text-fg-2 text-pretty">
        Iniciaste sesión correctamente, pero el portal no encontró capacidades para tu usuario.
        Pide a un administrador que te asigne un rol en Entra ID.
      </p>
      {email && <p className="font-data text-xs text-muted">{email}</p>}
      <div className="mt-2">
        <Button variant="secondary" onClick={() => logout()}>
          Cerrar sesión
        </Button>
      </div>
    </div>
  )
}
